import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class AppErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Application error caught by boundary:', error, errorInfo);
    
    // Log to console for debugging
    console.error('Error stack:', error.stack);
    console.error('Component stack:', errorInfo.componentStack);
    
    this.setState({
      error,
      errorInfo
    });
    
    // If it's an auth error, handle it specially
    if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
      console.error('WOULD CLEAR STORAGE BUT DISABLED FOR DEBUGGING - Auth error:', error.message);
      // TEMPORARILY DISABLED FOR DEBUGGING
      // localStorage.clear();
      // sessionStorage.clear();
      // window.location.href = '/login';
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Optionally reload the page
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // In production, show a user-friendly error page
      if (process.env.NODE_ENV === 'production') {
        return (
          <Container maxWidth="sm">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="100vh"
              textAlign="center"
              p={3}
            >
              <Typography variant="h4" gutterBottom>
                Oops! Something went wrong
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                We're sorry, but something unexpected happened. Please try refreshing the page.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={this.handleReset}
                sx={{ mt: 2 }}
              >
                Refresh Page
              </Button>
            </Box>
          </Container>
        );
      }
      
      // In development, show error details
      return (
        <Container maxWidth="md">
          <Box p={3}>
            <Typography variant="h4" color="error" gutterBottom>
              Application Error
            </Typography>
            <Typography variant="h6" gutterBottom>
              {this.state.error?.toString()}
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.875rem',
                mb: 2
              }}
            >
              {this.state.error?.stack}
            </Box>
            {this.state.errorInfo && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Component Stack:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem'
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </Box>
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReset}
              sx={{ mt: 2 }}
            >
              Reset
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;